import { type PaletteKey, PixelCanvas } from '../../lib';
import { grid, type Part } from '../body';
import type { Outfit } from '../outfits';
import { drawnLater } from './art';
import { domeLight, type Ellipse, fromPath, type HeadPicture, inEllipse, mask, type SetFlair, toneOf } from './mythic';

/**
 * The Abyssal Angler set (ADR-0160 amended, mythic): a diver the deep took back, in a brass diving helm grown an
 * anglerfish's lure, a barnacled diving suit, and the Tidecaller gun.
 *
 * The head is a brass diving helm (a mask over the whole head, its breastplate with it, so it covers the face standing
 * and lying): a dome lit from the top-left with a crisp sheen, a big porthole in a bezel of eight bolts, and behind the
 * dark glass two eyes in the team's light over a grin of needle fangs; barnacles on its back, a valve, the team's rubber
 * collar where it sits on its breastplate, and on its crown the root of a stalk. Its flair is the signature: the stalk
 * arching forward over the porthole and a bulb of the team's light swinging on its thread in front of the face (two
 * chains: a stiff rod and a pendulum), glowing and shedding motes; rings of bubbles out of the valve, the sea's light
 * shimmering on the brass, kelp hanging off its back. The suit is teal canvas, barnacled, a sleeve each side banded in
 * the team's colour, the team's belt hung with lead weights, lead boots; kelp caught on its sleeve.
 *
 * Rubric (`assetc mythic` at 5× and 8×, 1:1 on grass, lane and night, both teams, idle, run and dead): helm S5 R5 P4 L4
 * C4 A4 F4, suit S4 R4 P4 L4 C4 F4. Still weak: the lure's bulb is squares (the engine's dots), a lit gem more than a
 * round bulb at 5×; the suit shows little past the gun and the breastplate (its sleeves, kelp and belt carry it); the
 * dome's tones step in arcs at 8×.
 */

// --- The diving helm -------------------------------------------------------------------------------------------------

/** Brass by the light on it: a sheen, yellow, gold, burnt orange turned away, brown in its shade. */
const BRASS = [
  ['y', 0.9],
  ['Y', 0.4],
  ['U', -0.02],
] as const;
/** The dome's sheen: a crisp arc where it faces the light, this far out (of its radius) and this wide (radians). */
const SHEEN = [0.68, 0.34] as const;
/**
 * From the head's middle (u forward, v down): the bonnet's dome (round over the top, nearly straight down its sides, as
 * a real helm's is, so it covers the whole face) and the breastplate it is bolted to, over the shoulders.
 */
const DOME: Ellipse = [0, -2.4, 20.8, 18.6];
const BREAST: Ellipse = [0.5, 17.5, 19.5, 5.8];
/** The porthole's glass (turned a little away: narrower than tall), and its brass bezel's width. */
const PORT: Ellipse = [8, 1.5, 9.8, 10.6];
const BEZEL = 3;
/** The eyes behind the glass, near and far: [u, v, rx, ry, how the brow slants down to the nose]. */
const EYES: readonly (readonly [number, number, number, number, number])[] = [
  [4.4, 1.4, 2.7, 3.2, 0.45],
  [12.6, 1.4, 2.1, 3, -0.45],
];
/** Fangs in the dark under them: [u, v at the root, length (negative: pointing up)]. */
const FANGS: readonly (readonly [number, number, number])[] = [
  [2.5, 6.6, 2.6],
  [6.5, 7, 3.2],
  [10.5, 7, 2.8],
  [14, 6.6, 2],
  [4.5, 10.6, -2.2],
  [8.5, 10.9, -2.6],
  [12.3, 10.6, -2],
];
/** Barnacles grown on the back of the dome: [u, v]. */
const BARNACLES = [
  [-16.6, 5.4],
  [-13.4, 9.2],
  [-17.4, 10.2],
  [-14.4, 3.2],
] as const;
/** The valve on the helm's back, where its bubbles come out: [u0, u1, v0, v1]. */
const VALVE = [-13, -9, -21, -15] as const;
/** The lure's stalk where it grows out of the helm's crown (its flair goes on from its tip). */
const STALK = [5, -16, 6, -19.2, 7.6, -21.6];

/** In the dome: an ellipse over its middle, a superellipse under it. */
function inDome(u: number, v: number): boolean {
  const [cx, cy, rx, ry] = DOME;
  if (v <= cy) return inEllipse(u, v, DOME) <= 1;
  return ((u - cx) / rx) ** 4 + ((v - cy) / ry) ** 4 <= 1;
}

const helm: HeadPicture = (u, v, lx, ly) => {
  // The lure's stalk, plum flesh out of the brass.
  if (fromPath(u, v, STALK) < 1.5) return (u - 6) * lx + (v + 19) * ly > 0.3 ? 'p' : 'P';
  // The valve: a short brass pipe out of its back, capped.
  const [u0, u1, v0, v1] = VALVE;
  if (u >= u0 && u <= u1 && v >= v0 && v <= v1) {
    if (v < v0 + 1.6) return v < v0 + 0.8 ? 'y' : 'U';
    return u < u0 + 1.2 ? 'y' : u > u1 - 1.4 ? 'U' : 'Y';
  }
  if (!inDome(u, v)) {
    if (inEllipse(u, v, BREAST) > 1) return null;
    // The breastplate: the team's rubber collar where the dome sits on it, bolts round its rim, brass.
    if (inDome(u, v - 1.1)) return u < -6 ? 'c' : 'b';
    if (inDome(u, v - 2.2)) return 'B';
    const r = inEllipse(u, v, BREAST);
    if (r > 0.62 && r < 0.86 && v > BREAST[1] && Math.abs(((u + 40.5) % 5) - 2.5) < 0.7) return 'e';
    return toneOf(domeLight(u, v, BREAST, lx, ly) - 0.1, BRASS, 'n');
  }
  // The porthole: dark glass in a bezel of bolted brass.
  const [pu, pv, prx, pry] = PORT;
  const glass = inEllipse(u, v, PORT);
  const rim = inEllipse(u, v, [pu, pv, prx + BEZEL, pry + BEZEL]);
  if (rim <= 1 && glass > 1) {
    const [du, dv] = [(u - pu) / prx, (v - pv) / pry];
    const a = Math.atan2(dv, du);
    // Eight bolts round it.
    for (let k = 0; k < 8; k++) {
      const b = (k / 8) * Math.PI * 2 + Math.PI / 8;
      const [bu, bv] = [pu + Math.cos(b) * (prx + BEZEL / 2), pv + Math.sin(b) * (pry + BEZEL / 2)];
      if (Math.hypot(u - bu, v - bv) < 1.3) return (u - bu) * lx + (v - bv) * ly > -0.2 ? 'e' : 'n';
    }
    const lit = Math.cos(a) * lx + Math.sin(a) * ly;
    return lit > 0.55 ? 'y' : lit > -0.1 ? 'Y' : lit > -0.7 ? 'U' : 'n';
  }
  if (glass <= 1) {
    for (const [eu, ev, rx, ry, slant] of EYES) {
      const du = u - eu;
      const brow = ev - ry + 1.6 + du * slant;
      const d = Math.hypot(du / rx, (v - ev) / ry);
      if (d < 1.45 && v >= brow) return d < 0.45 ? 'w' : d < 1 ? 'c' : 'b';
    }
    const [du, dv] = [(u - pu) / prx, (v - pv) / pry];
    const r = Math.hypot(du, dv);
    const a = Math.atan2(dv, du);
    // The bezel's shadow on the glass along its lit side; a curved reflection high on it; light through it low.
    if (r > 0.86 && du * lx + dv * ly > 0.15) return 'k';
    if (r > 0.58 && r < 0.76 && a > -2.75 && a < -1.75) return a > -2.45 && a < -2.05 ? 'w' : '5';
    // Fangs in the dark: lit at the root, fading to their points.
    for (const [fu, fv, len] of FANGS) {
      const t = (v - fv) / len;
      if (t < 0 || t > 1) continue;
      if (Math.abs(u - fu) < 0.9 * (1 - t) + 0.3) return t < 0.5 ? '4' : '3';
    }
    if (r > 0.6 && du * lx + dv * ly < -0.55) return '2';
    return '1';
  }
  // Barnacles on its back: grey cones, a dark mouth at each top, lit on their top-left.
  for (const [bu, bv] of BARNACLES) {
    const [du, dv] = [u - bu, v - bv];
    const d = Math.hypot(du, dv);
    if (d >= 1.7) continue;
    if (d < 0.6) return '2';
    const lit = du * lx + dv * ly;
    return lit > 0.5 ? '5' : lit > -0.6 ? '4' : '3';
  }
  // Brass, its sheen a crisp arc round the dome facing the light (so the lying head's turns with it).
  const [cu, cv, rx, ry] = DOME;
  const [nu, nv] = [(u - cu) / rx, (v - cv) / ry];
  const off = Math.abs(Math.atan2(nv, nu) - Math.atan2(ly, lx));
  const away = Math.min(off, Math.PI * 2 - off);
  if (Math.abs(Math.hypot(nu, nv) - SHEEN[0]) < 0.045 && away < SHEEN[1]) return away < 0.12 ? 'w' : 'e';
  return toneOf(domeLight(u, v, DOME, lx, ly), BRASS, 'n');
};

// --- The diving suit -------------------------------------------------------------------------------------------------

/** Canvas the sea has had: deep teal, lit along its top-left. */
const CANVAS = ['D', 'G', 'G', 'g'] as const;

/**
 * The suit standing (the frame's x 5-55, y 39-55): teal canvas with a sleeve on each side, barnacles on it, a bolted
 * brass corselet over the shoulders, the team's band round each sleeve and the team's belt hung with lead weights.
 */
function suit(): Part {
  const [x0, y0] = [5, 39];
  const c = new PixelCanvas(51, 17);
  const at = (x: number, y: number, k: PaletteKey) => c.set(x - x0, y - y0, k);
  const [deep, shade, mid, lit] = CANVAS;
  for (let y = 44; y <= 55; y++)
    for (let x = 16; x <= 43; x++) at(x, y, x <= 17 ? lit : x >= 41 || y >= 54 ? deep : x >= 38 ? shade : mid);
  // The sleeves, the far one in shade, each with the team's band.
  for (const [xa, far] of [
    [5, false],
    [46, true],
  ] as const)
    for (let y = 44; y <= 55; y++)
      for (let x = xa; x <= xa + 8; x++) {
        if ((y === 44 || y === 55) && (x === xa || x === xa + 8)) continue;
        const band = y >= 50 && y <= 52;
        if (band) at(x, y, y === 50 ? 'c' : y === 52 ? 'B' : 'b');
        else at(x, y, !far && x <= xa + 1 ? lit : x >= xa + 7 || y === 55 ? deep : far ? shade : mid);
      }
  // The corselet: a brass collar plate over the shoulders, lit from the top-left, bolted round its rim.
  const plate: Ellipse = [30, 44, 19.5, 6.5];
  for (let y = 38; y <= 51; y++)
    for (let x = 9; x <= 51; x++) {
      const r = inEllipse(x + 0.5, y + 0.5, plate);
      if (r > 1) continue;
      const l = domeLight(x + 0.5, y + 0.5, plate, -Math.SQRT1_2, -Math.SQRT1_2);
      at(x, y, r > 0.8 && l < 0.2 ? 'n' : toneOf(l, BRASS, 'n'));
    }
  for (let k = 0; k < 9; k++) {
    const a = Math.PI * (0.05 + (k / 8) * 0.9);
    const [x, y] = [Math.round(30 + Math.cos(a) * 17 - 0.5), Math.round(44 + Math.sin(a) * 5 - 0.5)];
    at(x, y, 'e');
    at(x + 1, y + 1, 'U');
  }
  // Barnacles on the canvas: grey, lit on their top-left, a dark mouth in each.
  for (const [x, y] of [
    [7, 46],
    [10, 54],
    [17, 50],
  ] as const) {
    at(x, y, '5');
    at(x + 1, y, '4');
    at(x, y + 1, '4');
    at(x + 1, y + 1, '2');
  }
  // The team's belt, hung with lead weights.
  for (let x = 16; x <= 43; x++) {
    at(x, 53, 'c');
    at(x, 54, 'b');
    at(x, 55, 'B');
  }
  for (const xa of [18, 38])
    for (let y = 52; y <= 55; y++)
      for (let x = xa; x <= xa + 4; x++)
        at(x, y, y === 52 ? '4' : x === xa ? '3' : x === xa + 4 || y === 55 ? '1' : '2');
  return [c, x0, y0];
}

/** The suit lying (right of the legs, under the head): the team's belt at the waist (left), the corselet at the neck. */
const DEAD_SUIT = grid(`
  ....DGUY
  ...DGGYy
  cDGGGGYy
  b32GgGYe
  b32GGGYy
  B32GGgUY
  bDGGGGUY
  bGG5GGYy
  bGGGGGYy
  BGGGgGUY
  bGGGGGYy
  b32GGGYy
  b32GGGUY
  BDGGGGYU
  bGGGGGUY
  bGGG5GUY
  bDDDDDUU
  ....DDUU
`);

/** Lead-soled boots under the team's trousers. */
const DIVING_SUIT = (): Outfit => ({ torso: suit(), dead: [DEAD_SUIT, 24, 51], legs: { n: '3', N: '2' } });

// --- Tidecaller on every other gun -----------------------------------------------------------------------------------

/**
 * The Tidecaller finish (the default gun is its own, `pollinator_angler`): the steel gone to verdigris (deep teal-green,
 * a sea-green glint along its edges), dark brass where the wood was, the brass polished bright; the darkest steps stay
 * dark for the outline.
 */
const TIDECALLER = {
  '2': '1',
  '3': 'D',
  '4': 'G',
  '5': 'l',
  m: 'U',
  t: 'Y',
  e: 'y',
  U: 'Y',
  Y: 'y',
  y: 'e',
} as const;

// --- Its flair -------------------------------------------------------------------------------------------------------

const WHITE = 0xffffff;
const INK = 0x181425;
/** The lure's stalk root to tip: plum flesh. */
const STALK_TONES = [0x3e2731, 0x68386c, 0x68386c, 0xb55088] as const;
/** The lure's bulb, its thread to its foot: the stalk's plum, then the team's light swelling. */
const BULB = [0x68386c, 'teamDark', 'teamLight', 'teamLight', 'team'] as const;
const KELP = [0x265c42, 0x63c74d, 0x63c74d, 0x3e8948] as const;
const BUBBLE = [WHITE, 0xc0cbdc, 0x8b9bb4] as const;

/**
 * Bubbles out of the valve, frame by frame: one up and away, a bigger one after it, then a breath before the next (a
 * ring each, lit top-left; `.` nothing).
 */
const RISING = [
  `
  .......
  .......
  .......
  .......
  .......
  .......
  .......
  .......
  .......
  .......
  .......
  ..ww...
  .w..5..
  ..55...
  `,
  `
  .......
  .......
  .......
  .......
  .......
  .......
  .......
  ...ww..
  ..w..5.
  ...55..
  .......
  .......
  ...w...
  .......
  `,
  `
  .......
  .......
  .......
  ..ww...
  .w..5..
  ..55...
  .......
  .......
  .......
  ..ww...
  .w..5..
  .w..5..
  ..55...
  .......
  `,
  `
  ...w...
  .......
  .......
  .......
  .......
  ...ww..
  ..w..5.
  ..w..5.
  ...55..
  .......
  .......
  .......
  .......
  .......
  `,
  `
  .......
  ..ww...
  .w..5..
  .w..5..
  ..55...
  .......
  .......
  .......
  .......
  .......
  .......
  .......
  .......
  .......
  `,
  '.',
  '.',
];

/** A strand of kelp caught on it. */
const kelp = (x: number, y: number, n: number, phase: number, on: 'torso' | 'head', layer: 'back' | 'front') =>
  ({
    kind: 'chain',
    at: { on, x, y },
    n,
    seg: 2.6,
    dir: Math.PI * 0.56,
    curl: 0.03,
    stiff: 3.5,
    g: 160,
    drag: 0.08,
    sway: [0.24, 0.45, phase],
    w: [1, 2, 1],
    shape: 'dots',
    tones: KELP,
    rim: 0x193c3e,
    layer,
  }) as const;

const FLAIR: SetFlair = {
  head: [
    // The lure: a stiff stalk out of the crown arching over the porthole, its bulb swinging on a thread in front of the
    // face in the team's light, glowing and shedding motes.
    {
      kind: 'pulse',
      at: { on: 'head', x: 59, y: 19 },
      r: [9, 13],
      alpha: [0.4, 0.65],
      period: 1.3,
      tone: 'teamLight',
      layer: 'front',
    },
    {
      kind: 'chain',
      at: { on: 'head', x: 36, y: 9 },
      n: 11,
      seg: 3,
      dir: -1,
      curl: 0.26,
      stiff: 26,
      g: 40,
      drag: 0.05,
      sway: [0.035, 0.35, 0],
      w: [3, 2, 2],
      shape: 'dots',
      tones: STALK_TONES,
      layer: 'front',
    },
    {
      kind: 'chain',
      at: { on: 'head', x: 59, y: 14.5 },
      n: 3,
      seg: 2.4,
      dir: Math.PI / 2,
      stiff: 4,
      g: 420,
      drag: 0.06,
      sway: [0.28, 0.5, 0.25],
      w: [2, 7, 4],
      shape: 'dots',
      tones: BULB,
      rim: INK,
      lit: WHITE,
      shed: {
        rate: 3,
        v: [-4, 4, -6, 6],
        g: -4,
        drag: 0.4,
        wobble: [1, 1.5],
        life: [0.6, 1.2],
        size: [1, 1],
        tones: ['teamLight', 'team'],
        add: true,
        glow: 5,
      },
      layer: 'front',
    },
    // The eyes behind the glass.
    {
      kind: 'pulse',
      at: { on: 'head', x: 38, y: 30 },
      r: [6, 8],
      alpha: [0.15, 0.3],
      period: 1.7,
      tone: 'teamLight',
      layer: 'front',
    },
    // Bubbles out of the valve: rings rising, and specks trailing off.
    {
      kind: 'glyph',
      at: { on: 'head', x: 16, y: -7 },
      frames: RISING,
      tones: { w: WHITE, '5': 0xc0cbdc },
      hold: [0.12, 0.17],
      layer: 'back',
    },
    {
      kind: 'emit',
      at: { on: 'head', x: 19, y: -4 },
      box: [3, 2],
      rate: 1.2,
      v: [-4, 3, -14, -8],
      carry: 0.3,
      g: -18,
      drag: 0.6,
      wobble: [1.2, 2.2],
      life: [0.6, 1.1],
      size: [1, 2],
      tones: BUBBLE,
      layer: 'back',
    },
    // Light off the sea's surface dancing on the brass: a shimmer breathing, glints coming and going.
    {
      kind: 'pulse',
      at: { on: 'head', x: 26, y: 22 },
      r: [18, 23],
      alpha: [0.03, 0.13],
      period: 2.3,
      tone: WHITE,
      layer: 'front',
    },
    {
      kind: 'twinkle',
      at: { on: 'head', x: 26, y: 24 },
      box: [30, 24],
      round: true,
      n: 4,
      period: [0.9, 1.7],
      tones: [WHITE, 0xfee761],
      drift: [4, 3],
      add: true,
      layer: 'front',
    },
    kelp(9, 31, 6, 0.3, 'head', 'back'),
  ],
  outfit: [kelp(9, 47, 6, 0, 'torso', 'front'), kelp(15, 54, 4, 0.55, 'torso', 'back')],
  gun: [
    // Bubbles out of the vent over its porthole.
    {
      kind: 'emit',
      at: { on: 'gun', x: 37, y: 8 },
      box: [2, 1],
      rate: 2.5,
      v: [-3, 3, -14, -8],
      g: -16,
      drag: 0.6,
      wobble: [1, 2.5],
      life: [0.5, 0.9],
      size: [1, 2],
      tones: BUBBLE,
      layer: 'gun',
    },
  ],
};

/** The Abyssal Angler set (ADR-0160 amended). */
export const ANGLER = drawnLater({ head: () => mask(helm), outfit: DIVING_SUIT, gun: TIDECALLER, flair: FLAIR });
